import { AppUser, GetUserInput, SwitchUserRoleInput } from '@/types/userTypes';
import {
  RedirectLoginOptions
} from '@auth0/auth0-react';

// Define a more precise type for the getAccessTokenSilently function if desired
type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;

type LoginWithRedirectType = (options?: RedirectLoginOptions) => Promise<void>;

// Utility functions for authentication state management

const isIncognitoMode = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Test localStorage availability (basic incognito detection)
    try {
      localStorage.setItem('__test__', 'test');
      localStorage.removeItem('__test__');

      // Additional check using requestFileSystem (Chrome)
      if ('webkitRequestFileSystem' in window) {
        (window as any).webkitRequestFileSystem(
          0,
          0,
          () => resolve(false), // Not incognito
          () => resolve(true) // Incognito
        );
      } else {
        // Fallback: check if storage quota is very low (Firefox incognito)
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          navigator.storage.estimate().then((quota) => {
            // In incognito mode, quota is typically very limited
            resolve((quota.quota ?? 0) < 120000000); // Less than ~120MB suggests incognito
          });
        } else {
          resolve(false); // Assume not incognito if we can't detect
        }
      }
    } catch {
      resolve(true); // If localStorage fails, likely incognito
    }
  });
};

// Error types for better error handling
export enum AuthenticationError {
  POPUP_BLOCKED = 'POPUP_BLOCKED',
  CONSENT_REQUIRED = 'CONSENT_REQUIRED',
  LOGIN_REQUIRED = 'LOGIN_REQUIRED',
  INTERACTION_REQUIRED = 'INTERACTION_REQUIRED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

const categorizeAuthError = (error: any): AuthenticationError => {
  const errorString =
    error?.message?.toLowerCase() || error?.error?.toLowerCase() || '';

  if (
    errorString.includes('popup') ||
    errorString.includes('blocked') ||
    error?.message?.includes('null')
  ) {
    return AuthenticationError.POPUP_BLOCKED;
  }

  if (error?.error === 'consent_required') {
    return AuthenticationError.CONSENT_REQUIRED;
  }

  if (error?.error === 'login_required') {
    return AuthenticationError.LOGIN_REQUIRED;
  }

  if (error?.error === 'interaction_required') {
    return AuthenticationError.INTERACTION_REQUIRED;
  }

  // Map missing refresh token to login required so UI can prompt re-auth
  if (
    errorString.includes('missing refresh token') ||
    errorString.includes('missingrefresh') ||
    errorString.includes('refresh token')
  ) {
    return AuthenticationError.LOGIN_REQUIRED;
  }

  if (errorString.includes('network') || errorString.includes('fetch')) {
    return AuthenticationError.NETWORK_ERROR;
  }

  return AuthenticationError.UNKNOWN_ERROR;
};

// Utility to get the base URL, can be from .env
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const findOrCreateUser = async (
  input: GetUserInput,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  loginWithRedirect: LoginWithRedirectType,
  retryCount: number = 0 // Add retry tracking
): Promise<AppUser | null> => {
  const MAX_RETRIES = 2;
  let accessToken = '';
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE,
      scope: 'openid profile email offline_access'
    }
  };

  // Check if we're in incognito mode for better strategy selection
  const isIncognito = await isIncognitoMode();
  console.log('Authentication attempt in incognito mode:', isIncognito);

  try {
    // Try to get the token silently first
    accessToken = await getAccessTokenSilently(authParams);
    console.log('Token obtained silently, length:', accessToken.length);
  } catch (error: any) {
    console.warn('Silent token acquisition failed:', error);
    const errorType = categorizeAuthError(error);
    console.log('Categorized error type:', errorType);

    // Check if the error indicates consent is required or interaction is needed
    if (
      errorType === AuthenticationError.CONSENT_REQUIRED ||
      errorType === AuthenticationError.LOGIN_REQUIRED ||
      errorType === AuthenticationError.INTERACTION_REQUIRED
    ) {
      console.log('Falling back to login redirect...');

      try {
        await loginWithRedirect({
          authorizationParams: {
            audience: import.meta.env.VITE_API_AUTH0_AUDIENCE,
            scope: 'openid profile email offline_access',
            prompt: 'login'
          }
        });
        // Throw a specific error to halt execution flow in the caller
        throw new Error('REDIRECTING_TO_LOGIN');
      } catch (redirectError) {
        // If it's the specific error we just threw, rethrow it
        if ((redirectError as Error).message === 'REDIRECTING_TO_LOGIN') {
            throw redirectError;
        }
        
        console.error('Error during login redirect:', redirectError);
        throw new Error(
          `Authentication failed and redirect failed. Please try refreshing the page.`
        );
      }
    } else if (
      errorType === AuthenticationError.NETWORK_ERROR &&
      retryCount < MAX_RETRIES
    ) {
      // Only retry on network errors, with a delay
      console.log(
        `Network error, retrying in 2 seconds... (attempt ${
          retryCount + 1
        }/${MAX_RETRIES})`
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return findOrCreateUser(
        input,
        getAccessTokenSilently,
        loginWithRedirect,
        retryCount + 1
      );
    } else {
      // Some other error, don't retry to avoid infinite loops
      console.error(
        'Error in findOrCreateUser (token acquisition):',
        error,
        'Error type:',
        errorType
      );
      throw new Error(
        `Authentication failed: ${errorType}. Please try refreshing the page.`
      );
    }
  }

  // Log token info for debugging
  // To inspect the token structure (once successfully obtained):
  // const decodedHeader = JSON.parse(atob(accessToken.split('.')[0]));
  // console.log('Decoded Token Header:', decodedHeader); // Should now be RS256 if consent is granted

  // For security, avoid logging the full token in production
  // console.log('Token:', accessToken);

  try {
    const response = await fetch(`${API_BASE_URL}/users/find-or-create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify(input)
    });

    if (response.status === 404 && input.id) {
      console.error(`User with ID '${input.id}' not found.`);
      return null;
    }
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        errorData.message ||
          `Failed to find or create user. Status: ${response.status}`
      );
    }
    return (await response.json()) as AppUser;
  } catch (error) {
    console.error('Error in findOrCreateUser (API call):', error);
    throw error;
  }
};

export const updateUserSelectedRole = async (
  input: SwitchUserRoleInput,
  getAccessTokenSilently: (options?: any) => Promise<string>,
  loginWithRedirect: LoginWithRedirectType,
  retryCount: number = 0
): Promise<AppUser | null> => {
  const MAX_RETRIES = 2;
  let accessToken = '';
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE,
      scope: 'openid profile email offline_access'
    }
  };

  try {
    // Try to get the token silently
    accessToken = await getAccessTokenSilently(authParams);
    console.log('Token obtained silently, length:', accessToken.length);
  } catch (error: any) {
    console.warn('Silent token acquisition failed:', error);
    const errorType = categorizeAuthError(error);

    // Check if the error indicates consent is required or interaction is needed
    if (
      errorType === AuthenticationError.CONSENT_REQUIRED ||
      errorType === AuthenticationError.LOGIN_REQUIRED ||
      errorType === AuthenticationError.INTERACTION_REQUIRED
    ) {
      console.log('Falling back to login redirect...');
      try {
        await loginWithRedirect({
          authorizationParams: {
            audience: import.meta.env.VITE_API_AUTH0_AUDIENCE,
            scope: 'openid profile email offline_access',
            prompt: 'login'
          }
        });
        throw new Error('REDIRECTING_TO_LOGIN');
      } catch (redirectError) {
        if ((redirectError as Error).message === 'REDIRECTING_TO_LOGIN') {
          throw redirectError;
        }
        console.error('Error during login redirect:', redirectError);
        throw new Error(
          `Authentication failed and redirect failed. Please try refreshing the page.`
        );
      }
    } else if (
      errorType === AuthenticationError.NETWORK_ERROR &&
      retryCount < MAX_RETRIES
    ) {
      // Only retry on network errors
      console.log(
        `Network error, retrying in 2 seconds... (attempt ${
          retryCount + 1
        }/${MAX_RETRIES})`
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return updateUserSelectedRole(
        input,
        getAccessTokenSilently,
        loginWithRedirect,
        retryCount + 1
      );
    } else {
      // Some other error, rethrow with better messaging
      console.error(
        'Error in updateUserSelectedRole (token acquisition):',
        error,
        'Error type:',
        errorType
      );
      throw new Error(
        `Authentication failed: ${errorType}. Please try refreshing the page.`
      );
    }
  }

  const response = await fetch(`${API_BASE_URL}/users/switch-role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(
      errorData.message ||
        `Failed to update selected role. Status: ${response.status}`
    );
  }
  return (await response.json()) as AppUser;
};
